import { useCallback, useMemo, useState } from 'react'

import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import { Text } from '@hhgtech/hhg-components/mantine'
import { LOCALE } from '@hhgtech/hhg-components/types'
import dayjs from 'dayjs'
import Cookies from 'js-cookie'

import { NextImageWrap } from 'components/atoms/imageWrap'
import { useHelloSite } from 'hooks/useHelloSite'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath } from 'interfaces/constants'
import { Frequency, SiteWideBanner } from 'interfaces/types'

import { StyledIcon, StyledsiteWideContainer } from './index.styled'

const SITE_WIDE_EXPIRES: Record<Frequency, number> = {
  daily: 1,
  weekly: 7,
  'bi-weekly': 14,
}

export type Props = {
  banner: SiteWideBanner
  isMobile?: boolean
  onClose?: () => void
}

const SiteWide = ({ banner, isMobile, onClose }: Props) => {
  const {
    link,
    icon,
    content,
    type,
    layoutType,
    bannerDesktop,
    bannerMobile,
    backgroundColor,
    bannerDisplayFrequency,
  } = banner
  const { locale } = useRouterExtend()
  const [show, setShow] = useState(true)
  const { hellositeFullPath } = useHelloSite()

  const formattedContent = content

  const cookieName = useMemo(() => `discover-disable-sitewide-${type}`, [type])
  const handleCLoseSitewide = useCallback(
    (event: React.MouseEvent<HTMLImageElement>) => {
      event.preventDefault()
      Cookies.set(cookieName, 'true', {
        expires: SITE_WIDE_EXPIRES[bannerDisplayFrequency],
        path: hellositeFullPath,
      })
      setShow(false)
      onClose?.()
    },
    [hellositeFullPath, cookieName, bannerDisplayFrequency],
  )

  const dataVarsEventLabel = /<a[^>]*href=["']([^"']*)["']/g.exec(
    formattedContent,
  )?.[1]

  const isVNPublicDay = useMemo(
    () =>
      locale === LOCALE.Vietnam &&
      dayjs().isBetween('2024-04-29', dayjs('2024-05-02')),
    [],
  )

  if (!formattedContent || !show) {
    return null
  }

  return (
    <StyledsiteWideContainer
      data-is-mobile={isMobile}
      data-is-collapse={!show}
      data-is-vn-public-day={isVNPublicDay}
      id="sitewide-container"
      bgColor={backgroundColor}
    >
      {isVNPublicDay && isMobile && (
        <img
          className="vn-bg"
          style={{
            position: 'absolute',
            zIndex: 0,
            top: 0,
            bottom: 0,
            width: 'auto',
          }}
          src={CommonGAssets.getAssetPath('sitewide/vn-public-mobile.png')}
          alt=""
          loading="lazy"
        />
      )}
      <a
        className={`no-replace-click`}
        data-event-category="Header Banner"
        data-event-action="Link Click"
        data-event-label={hellositeFullPath}
        href={link || dataVarsEventLabel}
        style={{
          ...(isVNPublicDay && {
            position: 'relative',
          }),
        }}
      >
        {layoutType === 'icon-text' ? (
          <>
            {icon && (
              <StyledIcon className="site-wide-content">
                <div className="image-wrapper">
                  <NextImageWrap
                    src={icon}
                    className="icon"
                    loading="lazy"
                    data-is-mobile={isMobile}
                    alt=""
                    width={64}
                    height={64}
                  />
                </div>
              </StyledIcon>
            )}
            <div className="site-wide-content">
              {formattedContent && (
                <Text
                  size="s3"
                  className="content"
                  data-is-mobile={isMobile}
                  dangerouslySetInnerHTML={{ __html: formattedContent }}
                  lineClamp={2}
                  styles={{
                    root: {
                      fontFamily: `${
                        process.env.NEXT_PUBLIC_DEPLOY_LOCALE === 'km-KH'
                          ? 'Battambang,'
                          : process.env.NEXT_PUBLIC_DEPLOY_LOCALE === 'th-TH'
                          ? 'Noto Sans Thai,'
                          : 'Inter,'
                      } -apple-system,
                    BlinkMacSystemFont, 'Segoe UI', Oxygen, Ubuntu, Cantarell,
                    'Helvetica Neue', sans-serif;`,
                    },
                  }}
                />
              )}
            </div>
          </>
        ) : (
          <NextImageWrap
            src={isMobile ? bannerMobile : bannerDesktop}
            className="banner-full"
            loading="lazy"
            data-is-mobile={isMobile}
            alt=""
            fill
          />
        )}
      </a>
      <img
        src={`${assetImagePath}/closeWhite.svg`}
        data-event-category="Header Banner"
        data-event-action="Close Click"
        data-vars-event-label={hellositeFullPath}
        className="close-icon"
        onClick={handleCLoseSitewide}
        alt=""
        loading="lazy"
      />
    </StyledsiteWideContainer>
  )
}

export { SiteWide }
